import { useState } from 'react';
import { MessageCircle, List, Palette, Sparkles, Gift, Shuffle, Clock } from 'lucide-react';
import { useTranslation } from '../utils/useTranslation';

interface Feature {
    id: string;
    icon: any;
    title: string;
    subtitle: string;
    description: string;
}

export function FeaturesShowcase({ onNavigate, onDemoRequest, onCreateGroup }: {
    onNavigate: (view: any) => void;
    onDemoRequest: () => void;
    onCreateGroup: () => void;
}) {
    const { t, currentLang } = useTranslation();
    const [activeTab, setActiveTab] = useState('draw');

    const getLangCode = (lang: string) => {
        const map: Record<string, string> = {
            'pt-BR': 'pt',
            'en-US': 'en',
            'es-ES': 'es',
            'fr-FR': 'fr',
            'de-DE': 'de',
            'it-IT': 'it',
            'ja-JP': 'ja',
            'ko-KR': 'ko',
            'zh-CN': 'zh'
        };
        return map[lang] || 'en';
    };

    const langCode = getLangCode(currentLang);

    const features: Feature[] = [
        {
            id: 'draw',
            icon: Shuffle,
            title: t('features.showcase.items.draw.title'),
            subtitle: t('features.showcase.items.draw.subtitle'),
            description: t('features.showcase.items.draw.description')
        },
        {
            id: 'messages',
            icon: MessageCircle,
            title: t('features.showcase.items.messages.title'),
            subtitle: t('features.showcase.items.messages.subtitle'),
            description: t('features.showcase.items.messages.description')
        },
        {
            id: 'reveal',
            icon: Clock,
            title: t('features.showcase.items.reveal.title'),
            subtitle: t('features.showcase.items.reveal.subtitle'),
            description: t('features.showcase.items.reveal.description')
        },
        {
            id: 'wishlist',
            icon: List,
            title: t('features.showcase.items.wishlist.title'),
            subtitle: t('features.showcase.items.wishlist.subtitle'),
            description: t('features.showcase.items.wishlist.description')
        },
        {
            id: 'themes',
            icon: Palette,
            title: t('features.showcase.items.themes.title'),
            subtitle: t('features.showcase.items.themes.subtitle'),
            description: t('features.showcase.items.themes.description')
        },
        {
            id: 'ai',
            icon: Sparkles,
            title: t('features.showcase.items.ai.title'),
            subtitle: t('features.showcase.items.ai.subtitle'),
            description: t('features.showcase.items.ai.description')
        }
    ];

    const activeFeature = features.find(f => f.id === activeTab) || features[0];
    const Icon = activeFeature.icon;

    const handleLearnMore = () => {
        // Map feature IDs to view names
        const viewMap: Record<string, string> = {
            'draw': 'feature-draw',
            'messages': 'feature-messages',
            'wishlist': 'feature-wishlist',
            'themes': 'feature-themes',
            'ai': 'feature-ai',
            'reveal': 'feature-draw' // Reveal is part of draw feature
        };

        const view = viewMap[activeTab];
        if (view) {
            onNavigate(view);
        }
    };

    return (
        <div className="py-20 bg-gradient-to-br from-gray-50 via-white to-purple-50">
            <div className="container mx-auto px-4 max-w-6xl">
                {/* Header */}
                <div className="text-center mb-12">
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                        {t('features.showcase.title')}
                    </h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        {t('features.showcase.subtitle')}
                    </p>
                </div>

                {/* Tabs */}
                <div className="flex flex-wrap justify-center gap-3 mb-12">
                    {features.map((feature) => {
                        const FeatureIcon = feature.icon;
                        return (
                            <button
                                key={feature.id}
                                onClick={() => setActiveTab(feature.id)}
                                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${activeTab === feature.id
                                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg scale-105'
                                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                                    }`}
                            >
                                <FeatureIcon className="w-5 h-5" />
                                <span className="hidden sm:inline">{feature.title}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Feature Card */}
                <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
                    <div className="grid md:grid-cols-2">
                        {/* Left Side - Content */}
                        <div className="flex flex-col justify-center p-8 md:p-12">
                            <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-600 px-4 py-2 rounded-full w-fit mb-6">
                                <Icon className="w-5 h-5" />
                                <span className="font-medium text-sm">{activeFeature.subtitle}</span>
                            </div>

                            <h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                                {activeFeature.title}
                            </h3>

                            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                                {activeFeature.description}
                            </p>

                            <div className="flex gap-4">
                                <button
                                    onClick={onDemoRequest}
                                    className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold hover:shadow-lg hover:scale-105 transition-all"
                                >
                                    {t('features.showcase.cta.demo')}
                                </button>
                                <button
                                    onClick={handleLearnMore}
                                    className="px-6 py-3 border-2 border-purple-600 text-purple-600 rounded-xl font-semibold hover:bg-purple-50 transition-all"
                                >
                                    {t('features.showcase.cta.learnMore')}
                                </button>
                            </div>
                        </div>

                        {/* Right Side - Image */}
                        <div className="relative min-h-[320px] md:min-h-full bg-gradient-to-br from-purple-50 to-pink-50">
                            <img
                                src={`/images/features/${activeFeature.id}-${langCode}.webp`}
                                alt={activeFeature.title}
                                className="absolute inset-0 w-full h-full object-cover object-bottom"
                            />
                        </div>
                    </div>
                </div>

                {/* Bottom CTA */}
                <div className="text-center mt-12">
                    <p className="text-gray-600 mb-4">
                        {t('features.showcase.cta.ready')}
                    </p>
                    <button
                        onClick={onCreateGroup}
                        className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full font-bold text-lg hover:shadow-xl hover:scale-105 transition-all"
                    >
                        <Gift className="w-6 h-6" />
                        {t('features.showcase.cta.create')}
                    </button>
                </div>
            </div>
        </div>
    );
}
